// Founder labelling for dish identity (plan tasks L5, L6, M6). One item at a time,
// keyboard-driven. The set is fixed and pre-drawn (not a queue) and blind: the
// server sends no model label and no stratum.
//
// Keys — pair: S same, D different, N neither side is a dish, A / B only that side ·
// single line: D dish, N not a dish ·
// ? skip · ← → move · 0 clear. A label advances to the next unlabelled item.
//
// Until Phase D this runs against a local backend on the working copy
// (DATABASE_PATH=<identity-copy.db>); production's identity tables are empty.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminShell } from '@/components/AdminShell';
import {
  getHumanLabelSet,
  setHumanLabel,
  type HumanLabelItem,
  type HumanLabelLine,
  type HumanLabelValue,
} from '@/api/admin';

const VALUE_TEXT: Record<HumanLabelValue, string> = {
  SAME: 'Same dish',
  DIFFERENT: 'Different',
  DISH: 'A dish',
  NOT_A_DISH: 'Not a dish',
  NOT_A_DISH_A: 'A is not a dish',
  NOT_A_DISH_B: 'B is not a dish',
  NOT_A_DISH_BOTH: 'Neither is a dish',
};

export function IdentityLabelPage() {
  const [params] = useSearchParams();
  const round = params.get('round') ?? 'r0';
  const calibrationOnly = params.get('calibration') !== '0';
  const queryKey = useMemo(() => ['admin', 'identity-labels', round, calibrationOnly], [round, calibrationOnly]);
  const queryClient = useQueryClient();

  const setQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => getHumanLabelSet(round, calibrationOnly, signal),
    staleTime: Infinity,
  });
  const items = setQuery.data?.items ?? [];
  const [pos, setPos] = useState<number | null>(null);

  // Start at the first unlabelled item once the set arrives.
  useEffect(() => {
    if (pos === null && items.length) {
      const first = items.findIndex((i) => i.label === null);
      setPos(first === -1 ? items.length : first);
    }
  }, [items, pos]);

  const nextUnlabelled = useCallback(
    (from: number, list: HumanLabelItem[]) => {
      for (let k = 1; k <= list.length; k++) {
        const j = (from + k) % list.length;
        if (list[j]!.label === null) return j;
      }
      return list.length; // done
    },
    [],
  );

  const mutation = useMutation({
    mutationFn: ({ item, value }: { item: number; value: HumanLabelValue | null }) =>
      setHumanLabel(round, item, value),
    onMutate: ({ item, value }) => {
      queryClient.setQueryData<{ round: string; items: HumanLabelItem[] }>(queryKey, (old) =>
        old && { ...old, items: old.items.map((i) => (i.item === item ? { ...i, label: value } : i)) },
      );
    },
    onError: () => void queryClient.invalidateQueries({ queryKey }),
  });

  const current = pos !== null && pos < items.length ? items[pos]! : null;
  const labelled = items.filter((i) => i.label !== null).length;

  const label = useCallback(
    (value: HumanLabelValue | null) => {
      if (!current || pos === null) return;
      mutation.mutate({ item: current.item, value });
      if (value === null) return;
      const after = items.map((i) => (i.item === current.item ? { ...i, label: value } : i));
      setPos(nextUnlabelled(pos, after));
    },
    [current, pos, items, mutation, nextUnlabelled],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || pos === null) return;
      const k = e.key.toLowerCase();
      if (k === 'arrowleft') setPos(Math.max(0, Math.min(pos, items.length) - 1));
      else if (k === 'arrowright') setPos(Math.min(items.length - 1, pos + 1));
      else if (!current) return;
      else if (k === '?' || k === '/') setPos(nextUnlabelled(pos, items));
      else if (k === '0') label(null);
      else if (current.b && k === 's') label('SAME');
      else if (current.b && k === 'd') label('DIFFERENT');
      else if (current.b && k === 'n') label('NOT_A_DISH_BOTH');
      else if (current.b && k === 'a') label('NOT_A_DISH_A');
      else if (current.b && k === 'b') label('NOT_A_DISH_B');
      else if (!current.b && k === 'd') label('DISH');
      else if (!current.b && k === 'n') label('NOT_A_DISH');
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pos, items, current, label, nextUnlabelled]);

  return (
    <AdminShell>
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Dish identity labels</h1>
        <p className="text-sm text-slate-500">
          round {round}
          {calibrationOnly ? ' · calibration' : ''} · {labelled}/{items.length} labelled
        </p>
      </div>
      <div className="mt-3 h-1.5 w-full rounded bg-slate-100">
        <div
          className="h-1.5 rounded bg-slate-700"
          style={{ width: items.length ? `${(100 * labelled) / items.length}%` : 0 }}
        />
      </div>

      {setQuery.isLoading && <p className="mt-8 text-slate-500">Loading…</p>}
      {setQuery.isError && (
        <p className="mt-8 text-red-700">Could not load the set: {(setQuery.error as Error).message}</p>
      )}
      {mutation.isError && (
        <p className="mt-4 text-red-700">Saving failed: {(mutation.error as Error).message}</p>
      )}
      {setQuery.data && items.length === 0 && (
        <p className="mt-8 text-slate-600">This round has no items to label.</p>
      )}
      {items.length > 0 && pos !== null && !current && (
        <div className="card mt-8 p-6">
          <p className="font-medium">The set is complete: {labelled} of {items.length} labelled.</p>
          <p className="mt-1 text-sm text-slate-500">Press ← to review earlier items.</p>
        </div>
      )}

      {current && pos !== null && (
        <section className="mt-8">
          <p className="text-sm text-slate-500">
            Item {pos + 1} of {items.length}
            {current.label && (
              <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-slate-700">
                your label: {VALUE_TEXT[current.label]}
              </span>
            )}
          </p>
          <p className="mt-2 text-lg font-medium">
            {current.b ? 'Are these the same dish?' : 'Is this line one dish?'}
          </p>
          <div className={`mt-4 grid gap-4 ${current.b ? 'md:grid-cols-2' : ''}`}>
            <LineCard tag={current.b ? 'A' : null} line={current.a} />
            {current.b && <LineCard tag="B" line={current.b} />}
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {current.b ? (
              <>
                <KeyButton k="S" text="Same" active={current.label === 'SAME'} onClick={() => label('SAME')} />
                <KeyButton k="D" text="Different" active={current.label === 'DIFFERENT'} onClick={() => label('DIFFERENT')} />
                <KeyButton k="N" text="Neither is a dish" active={current.label === 'NOT_A_DISH_BOTH'} onClick={() => label('NOT_A_DISH_BOTH')} />
                <KeyButton k="A" text="A not a dish" active={current.label === 'NOT_A_DISH_A'} onClick={() => label('NOT_A_DISH_A')} />
                <KeyButton k="B" text="B not a dish" active={current.label === 'NOT_A_DISH_B'} onClick={() => label('NOT_A_DISH_B')} />
              </>
            ) : (
              <>
                <KeyButton k="D" text="A dish" active={current.label === 'DISH'} onClick={() => label('DISH')} />
                <KeyButton k="N" text="Not a dish" active={current.label === 'NOT_A_DISH'} onClick={() => label('NOT_A_DISH')} />
              </>
            )}
            <span className="ml-4 self-center text-sm text-slate-500">? skip · ← → move · 0 clear</span>
          </div>
        </section>
      )}
    </AdminShell>
  );
}

function LineCard({ tag, line }: { tag: string | null; line: HumanLabelLine }) {
  return (
    <div className="card p-5">
      <p className="text-xs uppercase tracking-wide text-slate-400">
        {tag && <span className="mr-2 font-semibold text-slate-600">{tag}</span>}
        {line.restaurant}
        {line.section ? ` · ${line.section}` : ''}
      </p>
      <p className="mt-2 text-xl font-semibold text-slate-900">{line.title}</p>
      {line.description && <p className="mt-2 text-slate-700">{line.description}</p>}
      {line.ingredients.length > 0 && (
        <p className="mt-3 text-sm text-slate-600">
          <span className="text-slate-400">Ingredients: </span>
          {line.ingredients.join(', ')}
        </p>
      )}
      {line.declaredAllergens.length > 0 && (
        <p className="mt-1 text-sm text-slate-600">
          <span className="text-slate-400">Allergens: </span>
          {line.declaredAllergens.join(', ')}
        </p>
      )}
    </div>
  );
}

function KeyButton({ k, text, active, onClick }: { k: string; text: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" className={active ? 'btn-primary' : 'btn-outline'} onClick={onClick}>
      <kbd className="mr-2 font-mono text-xs opacity-70">{k}</kbd>
      {text}
    </button>
  );
}
